import type { AdminSimSession, TeamRef } from "@/lib/api";
import { bestRun, sessionRun } from "@/lib/scoring";

/**
 * Every attempt at a server-simulated challenge, arranged the way the course is: by team,
 * then by student, then the attempts. The attempt budget is the student's and the
 * leaderboard row is the team's, so both are visible at once.
 */

export interface StudentAttempts {
  key: string;
  email: string;
  name: string;
  team: TeamRef | null;
  /** Newest first, reservations included. */
  sessions: AdminSimSession[];
  /** Attempts spent: every session but a `pending` reservation, as the server counts. */
  used: number;
  running: number;
  suspect: number;
  /** The run the leaderboard would take from this student, if any scored. */
  best: AdminSimSession | null;
  /** Newest activity, for tie-breaking and "last seen". */
  latest: number;
}

export interface TeamAttempts {
  key: string;
  /** Null for the section of students without a team. */
  team: TeamRef | null;
  students: StudentAttempts[];
  /** The team's leaderboard run: the best of its members' bests. */
  best: AdminSimSession | null;
}

const NO_TEAM = "~";

function studentGroups(sessions: readonly AdminSimSession[]): StudentAttempts[] {
  const byEmail = new Map<string, AdminSimSession[]>();
  for (const s of sessions) {
    const list = byEmail.get(s.email);
    if (list) list.push(s);
    else byEmail.set(s.email, [s]);
  }
  return [...byEmail.entries()].map(([email, rows]) => {
    rows.sort((a, b) => b.started_at - a.started_at);
    const newest = rows[0];
    return {
      key: email,
      email,
      name: newest.name,
      team: newest.team ?? null,
      sessions: rows,
      used: rows.filter((s) => s.status !== "pending").length,
      running: rows.filter((s) => s.status === "running").length,
      suspect: rows.filter((s) => s.timing_suspect).length,
      best: bestRun(rows.map(sessionRun))?.session ?? null,
      latest: Math.max(...rows.map((s) => s.ended_at ?? s.connected_at ?? s.started_at)),
    };
  });
}

/**
 * Teams in name order with students in email order under each, and the students without a
 * team last, in one section. A stable order: a TA scanning the page during a competition
 * should find a team where it was a minute ago, whatever has happened since.
 */
export function groupAttempts(sessions: readonly AdminSimSession[]): TeamAttempts[] {
  const teams = new Map<string, TeamAttempts>();
  for (const student of studentGroups(sessions)) {
    const key = student.team?.id ?? NO_TEAM;
    let team = teams.get(key);
    if (!team) {
      team = { key, team: student.team, students: [], best: null };
      teams.set(key, team);
    }
    team.students.push(student);
  }
  const sections = [...teams.values()];
  for (const section of sections) {
    section.students.sort((a, b) => a.email.localeCompare(b.email));
    const bests = section.students.flatMap((s) => (s.best ? [s.best] : []));
    section.best = bestRun(bests.map(sessionRun))?.session ?? null;
  }
  return sections.sort((a, b) => {
    if (a.team === null) return 1;
    if (b.team === null) return -1;
    return a.team.name.localeCompare(b.team.name, undefined, { numeric: true });
  });
}

/**
 * Attempt numbers as the student's own page shows them: oldest spent attempt is 1; a
 * reservation nobody connected to has no number.
 */
export function attemptNumbers(sessions: readonly AdminSimSession[]): Map<string, number> {
  const spent = sessions
    .filter((s) => s.status !== "pending")
    .sort((a, b) => a.started_at - b.started_at);
  return new Map(spent.map((s, i) => [s.id, i + 1]));
}

/** Case-insensitive match on the student's email or name, or their team's name. */
export function matchesAttempts(student: StudentAttempts, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    student.email.toLowerCase().includes(q) ||
    student.name.toLowerCase().includes(q) ||
    (student.team?.name.toLowerCase().includes(q) ?? false)
  );
}

function csvCell(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** One line per attempt, with the team and whether the leaderboard counts it. */
export function attemptsCsv(sections: readonly TeamAttempts[]): string {
  const header = [
    "team",
    "email",
    "name",
    "attempt",
    "session_id",
    "job_id",
    "started_at",
    "ended_at",
    "status",
    "end_reason",
    "goal_tier",
    "points",
    "max_points",
    "nav_steps",
    "spl",
    "trans_error",
    "rot_error",
    "think_mean_ms",
    "reported_mean_ms",
    "rtt_min_ms",
    "timing_suspect",
    "sdk_version",
    "counted_for_leaderboard",
  ];
  const lines = [header.join(",")];
  for (const section of sections) {
    for (const student of section.students) {
      const numbers = attemptNumbers(student.sessions);
      for (const s of student.sessions) {
        lines.push(
          [
            section.team?.name ?? "",
            student.email,
            student.name,
            numbers.get(s.id) ?? "",
            s.id,
            s.job_id,
            new Date(s.started_at).toISOString(),
            s.ended_at === null ? "" : new Date(s.ended_at).toISOString(),
            s.status,
            s.end_reason,
            s.result?.goal_tier,
            s.result?.score.points,
            s.result?.score.max_points,
            s.result?.nav_steps,
            s.result?.spl,
            s.trans_error,
            s.rot_error,
            s.timing.think_mean_ms,
            s.timing.reported_mean_ms,
            s.rtt_min_ms,
            s.timing_suspect,
            s.sdk_version,
            section.best?.id === s.id,
          ]
            .map(csvCell)
            .join(","),
        );
      }
    }
  }
  return lines.join("\n");
}
